import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Dialog, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import clsx from "clsx";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import Button from "~/features/common/components/buttons/Button/index";
import Icon from "~/features/common/components/icons/Icon/index";
import { cn } from "~/features/common/utils/tailwind";

export default function ModalConfirm({
  title,
  children,
  open,
  setOpen,
  onConfirm,
  onCancel,
  confirmButtonLabel = "Save",
  cancelButtonLabel = "Cancel",
  disableSaveButton,
  successButton,
  primaryButton,
  removeCancelButton,
  isSaving,
  modalGrowHorizontal,
  modalClassName,
  testID,
}: {
  title?: string;
  children?: ReactNode;
  open?: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  disableSaveButton?: boolean;
  successButton?: boolean;
  primaryButton?: boolean;
  removeCancelButton?: boolean;
  isSaving?: boolean;
  modalGrowHorizontal?: boolean;
  modalClassName?: string;
  testID?: string;
}) {
  return (
    <Transition show={open}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={setOpen}>
        <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
          <TransitionChild>
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition duration-300 ease-out data-closed:opacity-0 data-leave:duration-200 data-leave:ease-in" />
          </TransitionChild>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>
          <TransitionChild>
            <div
              data-testid={testID ?? "confirmation-modal"}
              className={cn(
                "inline-block transform rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all dark:bg-black sm:my-8 sm:p-6 sm:align-middle",
                "duration-300 ease-out data-closed:opacity-0 data-closed:translate-y-4 data-closed:sm:translate-y-0 data-closed:sm:scale-95",
                "data-leave:duration-200 data-leave:ease-in",
                { "sm:w-full sm:max-w-3xl": !modalGrowHorizontal },
                modalClassName,
              )}>
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                {isSaving ? null : (
                  <button
                    type="button"
                    className="flex h-5 w-5 items-center justify-center rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:bg-black dark:text-gray-300 dark:hover:text-gray-50"
                    onClick={() => setOpen(false)}>
                    <span className="sr-only">Close</span>
                    <Icon icon={faTimes} />
                  </button>
                )}
              </div>
              <div className="sm:flex sm:justify-start">
                <div className="mt-3 w-full text-center sm:mt-0 sm:text-center">
                  <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-50">
                    {title}
                  </DialogTitle>
                  <div className="mt-4">{children}</div>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="mt-5 flex sm:mt-4">
                  <div className="w-fit">
                    <Button
                      type="submit"
                      destructive={!successButton && !primaryButton}
                      className={clsx(
                        "mr-2",
                        successButton && "bg-green-500 hover:bg-green-600 dark:bg-green-500 dark:hover:bg-green-600",
                        primaryButton && "bg-primary hover:bg-blue-600 dark:bg-primary dark:hover:bg-blue-600",
                      )}
                      onClick={onConfirm}
                      disabled={disableSaveButton ?? false}
                      data-testid="delete-btn"
                      isLoading={isSaving}>
                      {confirmButtonLabel}
                    </Button>
                  </div>
                  {!removeCancelButton && (
                    <div className="w-20">
                      <Button
                        type="button"
                        className="mr-2"
                        onClick={() => {
                          if (onCancel) {
                            onCancel();
                          }
                          setOpen(false);
                        }}>
                        {cancelButtonLabel}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
